import type { QueryKey } from '@tanstack/react-query';

import useInfiniteQuery from '../../../ReactQuery/useInfiniteQuery';
import { get } from '../../../redux/api';
import type { GetAddableUsersResponse } from '../../../../shared/types/routes/teams';

/**
 * Generate the query key for finding addable members for a team.
 */
export const getAddableMembersQueryKey = (
  teamId: number,
  pageSize: number,
  search?: string,
): QueryKey => ['teams', teamId, 'members', 'new', 'search', pageSize, search];

/**
 * Fetch the addable members for a team.
 */
function fetchAddableMembers({ pageParam = 0 }, teamId: number, pageSize: number, search?: string) {
  return get<GetAddableUsersResponse>(`/api/teams/${teamId}/members/addable`, false, {
    pageSize,
    page: pageParam,
    search,
  });
}

/**
 * Helper hook for fetching the matches of a given component in a user's revision.
 * @param teamId - The ID of the team to fetch addable members for.
 * @param pageSize - The number of addable members to fetch per page.
 * @param search - The search query to filter the addable members by.
 * @param shouldFetch - Whether to fetch the addable members or not. Used to prevent fetching until addable members are needed to be displayed.
 * @returns An infinite query for the addable members of the given team.
 */
export default function useAddableMembers(
  teamId: number,
  pageSize: number,
  search?: string,
  shouldFetch: boolean = true,
) {
  return useInfiniteQuery<GetAddableUsersResponse>({
    queryKey: getAddableMembersQueryKey(teamId, pageSize, search),
    queryFn: (context) => fetchAddableMembers(context, teamId, pageSize, search),
    enabled: shouldFetch,
  });
}
